import { useEffect, useState } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import Chip, { chipClasses } from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { useMutation } from 'react-query';

import { Button, Modal } from 'src/components';
import { ActionButton } from '../../styles';
import { UsersService } from 'src/services';
import { CouponCode, CouponDuration, isBlikOnlyCoupon } from 'src/models/Coupon';
import { useUserActions, useUserState } from 'src/contexts/UserContext';
import { ApiError, StripeCardError, StripeError } from 'src/errors';

const CodeInput = styled(TextField).attrs({
  variant: 'outlined',
  size: 'small',
  fullWidth: true,
})``;

const BlikInfo = () => (
  <Alert variant="outlined" severity="info" sx={{mt: 2}}>
    Jeżeli Twój bank nie obsługuje <a href="https://www.blik.com/platnosci-powtarzalne" target="_blank" rel="noopener noreferrer">Płatności powtarzalnych BLIK</a>, usuń kod BLIK, wpisz kod 15URO i dokończ rejestrację w cenie 15zł za pierwszy miesiąc.
  </Alert>
);

export const RedeemCode: React.FC = () => {
  const { confirmCouponRedeemed } = useUserActions();
  const { user } = useUserState()
  const [showDialog, setShowDialog] = useState(false);
  const [code, setCode] = useState('');
  useEffect(() => setCode(''), [showDialog]);

  const couponRedemption = useMutation(UsersService.redeemCoupon, {
    onError: (err: ApiError) => {
      if (err instanceof StripeCardError || err instanceof StripeError) {
        setPaymentError(true);
      } else {
        setError(true);
      }
    },
    onSuccess: () => {
      confirmCouponRedeemed();
      setSuccess(true);
    },
  });

  const clearCoupon = useMutation(UsersService.clearCoupon, {
    onSuccess: () => {
      confirmCouponRedeemed();
    },
  });

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const showBlikInfo = isBlikOnlyCoupon(user.coupon);

  return (
    <>
      {!success && user.coupon == null && (
        <ActionButton onClick={() => setShowDialog(true)}>
          Zrealizuj voucher / kod rabatowy
        </ActionButton>
      )}
      {user.coupon != null && (
        <div>
          <span style={{marginLeft: 8}}>Aktywny voucher / kod rabatowy:</span>&nbsp;
          <Chip
            label={user.coupon.code}
            color="success"
            variant="outlined"
            disabled={user.coupon.duration === CouponDuration.Forever}
            onDelete={() => clearCoupon.mutate()}
            sx={{
              [`& .${chipClasses.deleteIcon}`]: {
                fill: '#FF667D'
              }
            }}
          />
        </div>
      )}
      {showBlikInfo && <BlikInfo />}
      <Modal open={showDialog}>
        <Modal.Title>Zrealizuj voucher / kod rabatowy</Modal.Title>
        <Modal.Content>
          <p>Aby rozpocząć, wprowadź swój kod poniżej:</p>
          <CodeInput
            label="Twój kod"
            value={code}
            onChange={e => setCode(e.target.value.toLocaleUpperCase())}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="Anuluj"
            onClick={() => setShowDialog(false)}
            variant="minimal"
            color="primary"
            disabled={couponRedemption.isLoading}
          />
          <Button
            label="Zrealizuj kod"
            onClick={() => couponRedemption.mutate(code as CouponCode)}
            disabled={!code || success}
            pending={couponRedemption.isLoading}
          />
        </Modal.Actions>
      </Modal>

      <Modal open={showDialog && success}>
        <Modal.Title>Kod zrealizowany</Modal.Title>
        <Modal.Content>
          <p>Kod {code} został zrealizowany!</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="OK"
            onClick={() => setShowDialog(false)}
          />
        </Modal.Actions>
      </Modal>

      <Modal open={showDialog && error}>
        <Modal.Title>Błąd</Modal.Title>
        <Modal.Content>
          <p>Podany kod jest nieprawidłowy.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="Wróć"
            onClick={() => {
              setError(false);
              setCode('');
            }}
          />
        </Modal.Actions>
      </Modal>

      <Modal open={showDialog && paymentError}>
        <Modal.Title>Problem z płatnością</Modal.Title>
        <Modal.Content>
          <p>Wygląda na to, że mamy problem z Twoją kartą. Sprawdź saldo i spróbuj ponownie — jeśli problem się powtarza, skontaktuj się z nami.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="Wróć"
            onClick={() => {
              setPaymentError(false);
              setCode('');
            }}
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export type ErrorCouponResult =
  'unsupportedCoupon' |
  'unsupportedSubscription' |
  'incorrectSubscriptionStatus' |
  'incorrectInterval';

const messages = {
  promoApplied: () => (
    <p>Gratulacje! Super, że jesteś z nami!</p>
  ),
  subscriptionCreated: () => (
    <p>Gratulacje! Super, że jesteś z nami!</p>
  ),
  periodExtended: () => (
    <p>Gratulacje! Super, że jesteś z nami!</p>
  ),
  incorrectInterval: () => (
    <p>Aby wykorzystać specjalny kod, skontaktuj się z naszym zespołem na <a href="mailto:pomoc@portalyogi.pl">pomoc@portalyogi.pl</a>.</p>
  ),
  checkout: () => (
    <p>Wymagana akcja. Przejdź na stronę płatności</p>
  ),
};

export function RedeemPartnerCode() {
  const { confirmCouponRedeemed } = useUserActions();
  // const state = useUserState();
  const [showDialog, setShowDialog] = useState(false);
  const [code, setCode] = useState('');
  useEffect(() => setCode(''), [showDialog]);

  const couponRedemption = useMutation(UsersService.redeemCouponV2, {
    onError: (err: ApiError<{ message: string, error: string }>) => {
      if (err instanceof StripeCardError || err instanceof StripeError) {
        setPaymentError(true);
        return;
      }
      const data = (err.rawError as any).response?.data;
      if (data?.message === 'incorrectInterval') {
        setMessage(data.message);
      } else if (data?.message === 'paymentFailed') {
        setPaymentError(true);
      } else if (data?.message === 'unsupportedSubscription' || data?.message === 'pendingCancellation') {
        setActiveSubError(true);
      } else {
        setError(true);
      }
    },
    onSuccess: (res) => {
      setMessage(res.result);
      if (res.result === 'checkout') {
        window.location.href = res.url!;
      } else {
        confirmCouponRedeemed();
      }
    },
  });

  const [message, setMessage] = useState<keyof typeof messages>();
  const [error, setError] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const [activeSubError, setActiveSubError] = useState(false);
  const success = message != null;

  return (
    <>
      {!success && (
        <ActionButton onClick={() => setShowDialog(true)}>
          Twój specjalny kod
        </ActionButton>
      )}
      <Modal open={showDialog}>
        <Modal.Title>Twój specjalny kod</Modal.Title>
        <Modal.Content>
          <p>Aby rozpocząć, wprowadź swój kod poniżej:</p>
          <CodeInput
            label="Twój kod"
            value={code}
            onChange={e => setCode(e.target.value.toLocaleUpperCase())}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="Anuluj"
            onClick={() => setShowDialog(false)}
            variant="minimal"
            color="primary"
            disabled={couponRedemption.isLoading}
          />
          <Button
            label="Zrealizuj kod"
            onClick={() => couponRedemption.mutate(code as CouponCode)}
            disabled={!code || success}
            pending={couponRedemption.isLoading}
          />
        </Modal.Actions>
      </Modal>

      <Modal open={showDialog && success}>
        <Modal.Title>Kod zrealizowany</Modal.Title>
        <Modal.Content>
          {message && messages[message]()}
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="OK"
            onClick={() => setShowDialog(false)}
          />
        </Modal.Actions>
      </Modal>

      <Modal open={showDialog && error}>
        <Modal.Title>Błąd</Modal.Title>
        <Modal.Content>
          <p>Podany kod jest nieprawidłowy.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="Wróć"
            onClick={() => {
              setError(false);
              setCode('');
            }}
          />
        </Modal.Actions>
      </Modal>

      <Modal open={showDialog && paymentError}>
        <Modal.Title>Problem z płatnością</Modal.Title>
        <Modal.Content>
          <p>Wygląda na to, że mamy problem z Twoją kartą. Sprawdź saldo i spróbuj ponownie — jeśli problem się powtarza, skontaktuj się z nami.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="Wróć"
            onClick={() => {
              setPaymentError(false);
              setCode('');
            }}
          />
        </Modal.Actions>
      </Modal>

      <Modal open={showDialog && activeSubError}>
        <Modal.Title>Aktywna subskrypcja</Modal.Title>
        <Modal.Content>
          <p>Z tego kodu możesz skorzystać dopiero po wygaśnięciu obecnej subskrypcji.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            label="OK"
            onClick={() => {
              setActiveSubError(false);
              setCode('');
            }}
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};
